// src/features/dashboard/components/ActivityTimeline.tsx

import {
  useActivityTimeline,
} from '../hooks/Employee/useActivityTimeline';

import type {
  TimelineLegendItem,
  TimelineStat,
} from '../types/Employee/activityTimeline.types';

import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
} from 'recharts';

interface PieItem {
  name: string;
  value: number;
  color: string;
}

const FALLBACK_COLORS = [
  '#2563EB',
  '#10B981',
  '#F59E0B',
  '#EF4444',
  '#8B5CF6',
  '#06B6D4',
];

/* ============================================================
   VALUE PARSER
   ============================================================ */

const parseValue = (
  value: string,
): number => {
  if (!value) {
    return 0;
  }

  const match = value.match(/[\d.]+/);

  if (!match) {
    return 0;
  }

  return Number(match[0]);
};

/* ============================================================
   TOOLTIP
   ============================================================ */

const CustomTooltip = ({
  active,
  payload,
}: any) => {
  if (!active || !payload?.length) {
    return null;
  }

  const item = payload[0];

  return (
    <div
      className="
        relative
        rounded-[10px]
        border
        border-[#E4E7EC]
        bg-white
        px-3
        py-2
        pl-3
        shadow-lg
      "
      style={{
        borderLeftWidth: '4px',
        borderLeftColor:
          item.payload?.color ||
          '#98A2B3',
      }}
    >
      <div className="flex items-center gap-2">
        <span
          className="
            h-2.5
            w-2.5
            shrink-0
            rounded-full
          "
          style={{
            backgroundColor:
              item.payload?.color ||
              '#98A2B3',
          }}
        />

        <span
          className="
            text-[12px]
            font-medium
            text-[#344054]
          "
        >
          {item.name}
        </span>

        <span
          className="
            text-[12px]
            font-semibold
            text-[#101828]
          "
        >
          {item.value}
        </span>
      </div>
    </div>
  );
};

/* ============================================================
   COMPONENT
   ============================================================ */

const ActivityTimeline = () => {
  const {
    data,
    loading,
  } = useActivityTimeline();

  if (loading || !data) {
    return null;
  }

  /* ==========================================================
     LEGEND DATA
     ========================================================== */

  const legends: TimelineLegendItem[] =
    data.legends ?? [];

  const pieData: PieItem[] =
    legends
      .map((item, index) => ({
        name: item.label,
        value: parseValue(item.value),
        color:
          item.color ||
          FALLBACK_COLORS[
            index %
              FALLBACK_COLORS.length
          ],
      }))
      .filter(
        (item) => item.value > 0,
      );

  /* ==========================================================
     TOTAL
     ========================================================== */

  const total = pieData.reduce(
    (sum, item) =>
      sum + item.value,
    0,
  );

  /* ==========================================================
     RENDER
     ========================================================== */

  return (
    <div
      className="
        w-full
        min-w-0
        overflow-hidden
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        p-4
      "
    >
      {/* ======================================================
          HEADER
          ====================================================== */}

      <div
        className="
          mb-3
          flex
          min-w-0
          flex-wrap
          items-center
          gap-2
        "
      >
        <h2
          className="
            min-w-0
            truncate
            text-[16px]
            font-semibold
            leading-[20px]
            text-[#002B5B]
          "
        >
          Daily Activity Breakdown
        </h2>

        <span
          className="
            shrink-0
            rounded-full
            bg-[#EEF4FF]
            px-3
            py-[4px]
            text-[10px]
            font-medium
            text-[#2563EB]
          "
        >
          {data.loginInfo}
        </span>
      </div>

      {/* ======================================================
          MAIN CONTENT

          Same compact height as MonthlyHoursTrend.
          Pie + legend remain inside the card.
          ====================================================== */}

      <div
        className="
          grid
          w-full
          min-w-0
          grid-cols-1
          gap-3
          lg:grid-cols-[190px_minmax(0,1fr)]
          lg:items-center
        "
      >
        {/* ====================================================
            PIE
            ==================================================== */}

        <div
          className="
            relative
            flex
            h-[190px]
            min-h-[190px]
            w-full
            min-w-[190px]
            shrink-0
            items-center
            justify-center
          "
        >
          {pieData.length > 0 ? (
            <>
              <ResponsiveContainer
                width="100%"
                height="100%"
                minWidth={1}
                minHeight={1}
              >
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={2}
                    cornerRadius={4}
                    startAngle={90}
                    endAngle={-270}
                    stroke="#FFFFFF"
                    strokeWidth={3}
                    isAnimationActive={false}
                    animationDuration={0}
                  >
                    {pieData.map(
                      (
                        item,
                        index,
                      ) => (
                        <Cell
                          key={`activity-pie-${index}`}
                          fill={item.color}
                        />
                      ),
                    )}
                  </Pie>

                  <Tooltip
                    content={
                      <CustomTooltip />
                    }
                    wrapperStyle={{
                      zIndex: 20,
                    }}
                    offset={12}
                  />
                </PieChart>
              </ResponsiveContainer>

              {/* Center */}

              <div
                className="
                  pointer-events-none
                  absolute
                  left-1/2
                  top-1/2
                  flex
                  -translate-x-1/2
                  -translate-y-1/2
                  flex-col
                  items-center
                  justify-center
                "
              >
                <span
                  className="
                    text-[22px]
                    font-bold
                    leading-none
                    text-[#101828]
                  "
                >
                  {total}
                </span>

                <span
                  className="
                    mt-1
                    text-[9px]
                    font-medium
                    uppercase
                    tracking-[0.7px]
                    text-[#667085]
                  "
                >
                  Total
                </span>
              </div>
            </>
          ) : (
            <div
              className="
                flex
                h-[150px]
                w-[150px]
                items-center
                justify-center
                rounded-full
                border
                border-dashed
                border-[#D0D5DD]
                text-[11px]
                text-[#667085]
              "
            >
              No activity data
            </div>
          )}
        </div>

        {/* ====================================================
            LEGENDS
            ==================================================== */}

        <div
          className="
            grid
            min-w-0
            w-full
            grid-cols-1
            gap-2
          "
        >
          {pieData.map(
            (
              item,
              index,
            ) => {
              const percentage =
                total > 0
                  ? (
                      (item.value /
                        total) *
                      100
                    ).toFixed(1)
                  : '0.0';

              return (
                <div
                  key={`${item.name}-${index}`}
                  className="
                    min-w-0
                    w-full
                    overflow-hidden
                    rounded-[10px]
                    border
                    border-[#EAECF0]
                    bg-[#FCFCFD]
                    px-2.5
                    py-2
                  "
                >
                  {/* Name + Percentage */}

                  <div
                    className="
                      flex
                      min-w-0
                      items-center
                      justify-between
                      gap-2
                    "
                  >
                    <div
                      className="
                        flex
                        min-w-0
                        items-center
                        gap-1.5
                      "
                    >
                      <span
                        className="
                          h-2.5
                          w-2.5
                          shrink-0
                          rounded-full
                        "
                        style={{
                          backgroundColor:
                            item.color,
                        }}
                      />

                      <span
                        className="
                          min-w-0
                          truncate
                          text-[10px]
                          font-medium
                          text-[#344054]
                        "
                        title={item.name}
                      >
                        {item.name}
                      </span>
                    </div>

                    <span
                      className="
                        shrink-0
                        text-[10px]
                        font-semibold
                        text-[#101828]
                      "
                    >
                      {percentage}%
                    </span>
                  </div>

                  {/* Value */}

                  <div
                    className="
                      mt-1
                      text-[14px]
                      font-bold
                      leading-[18px]
                      text-[#101828]
                    "
                  >
                    {item.value}
                  </div>

                  {/* Progress */}

                  <div
                    className="
                      mt-1
                      h-[3px]
                      w-full
                      overflow-hidden
                      rounded-full
                      bg-[#EAECF0]
                    "
                  >
                    <div
                      className="
                        h-full
                        rounded-full
                      "
                      style={{
                        width: `${Math.min(
                          Number(
                            percentage,
                          ),
                          100,
                        )}%`,
                        backgroundColor:
                          item.color,
                      }}
                    />
                  </div>
                </div>
              );
            },
          )}
        </div>
      </div>

      {/* ======================================================
          DIVIDER
          ====================================================== */}

      <div
        className="
          my-3
          border-t
          border-dashed
          border-[#D0D5DD]
        "
      />

      {/* ======================================================
          STATS
          ====================================================== */}

      <div
        className="
          grid
          grid-cols-2
          gap-y-3
          lg:grid-cols-4
        "
      >
        {(data.stats ?? []).map(
          (
            item: TimelineStat,
          ) => (
            <div
              key={item.label}
              className="
                min-w-0
              "
            >
              <p
                className="
                  mb-1
                  text-[9px]
                  font-medium
                  uppercase
                  tracking-[0.8px]
                  text-[#667085]
                "
              >
                {item.label}
              </p>

              <div
                className="
                  flex
                  items-end
                  gap-1
                "
              >
                <h3
                  className="
                    text-[16px]
                    font-semibold
                    leading-none
                    text-[#101828]
                  "
                >
                  {item.value}
                </h3>

                {item.extra && (
                  <span
                    className="
                      text-[10px]
                      text-[#667085]
                    "
                  >
                    {item.extra}
                  </span>
                )}
              </div>
            </div>
          ),
        )}
      </div>
    </div>
  );
};

export default ActivityTimeline;